import {
  useStrings,
  useStringsWithPlaceholders,
} from "@1js/localization-components";
import {
  PromptAuthor,
  PromptBadges,
  usePromptFilterLocalizations,
  type Badge,
  type Prompt,
} from "@1js/pl-card-components";
import { isExtensionPrompt } from "@1js/pl-types";
import { Text, Tooltip, mergeClasses, useId } from "@fluentui/react-components";
import { Info16Regular } from "@fluentui/react-icons";
import * as React from "react";
import { useFeatureEnablement } from "../../hooks/useFeatureEnablement";
import { useHandoffService } from "../../hooks/useHandoffService";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { WebPrompt } from "../data";
import {
  DetailDisplayCategoryDescription,
  DetailProductDescription,
  DetailProductDescriptionAlsoWorksIn,
  DetailProductDescriptionAlsoWorksInInfoLabel,
  ExtensionPromptDescriptionInfoLabel,
} from "./PromptDescription.web.string";
import { useStyles } from "./PromptDescription.web.styles";

export type PromptDescriptionProps = {
  prompt: WebPrompt | Prompt;
};

export const PromptDescription: React.FC<PromptDescriptionProps> = ({
  prompt,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    DetailProductDescription,
    DetailProductDescriptionAlsoWorksIn,
    DetailProductDescriptionAlsoWorksInInfoLabel,
    DetailDisplayCategoryDescription,
  });
  const localizationsWithPlaceholders = useStringsWithPlaceholders({
    ExtensionPromptDescriptionInfoLabel,
  });
  const { state } = React.useContext(LibraryContext);

  const { isTryInOnAuthed } = useFeatureEnablement();
  const { facetValueToLabel } = usePromptFilterLocalizations();

  const badges: Badge[] = prompt.Products.map((product: string) => {
    return {
      id: product,
    };
  });

  const { isHandoffSupported } = useHandoffService();

  const tryInBadgesCount = prompt.Products.filter((badge: string) =>
    isHandoffSupported(badge, prompt)
  ).length;
  const tryInByCopyBadges: Badge[] = prompt.Products.filter(
    (badge: string) => !isHandoffSupported(badge, prompt)
  ).map((product) => {
    return {
      id: product,
    };
  });

  const displayCategoryBadges: Badge[] = [
    {
      id: facetValueToLabel(prompt.DisplayCategory),
    },
  ];

  const alsoWorksInTooltipContentId = useId("AlsoWorksInTooltipContent");
  const [isAlsoWorksInTooltipVisible, setAlsoWorksInTooltipVisible] =
    React.useState(false);

  const isExtension: boolean = isExtensionPrompt(prompt);
  const showToolTip =
    isExtension || (isTryInOnAuthed && tryInByCopyBadges.length > 0);

  return (
    <div className={styles.container}>
      {!isExtension && !isTryInOnAuthed && badges.length > 0 && (
        <div className={styles.badge} data-testid="productbadges">
          <Text className={styles.title}>
            {localizations.DetailProductDescription()}
          </Text>
          <PromptBadges
            badges={badges}
            maxNumber={5}
            displayMode="iconAndText"
          />
        </div>
      )}
      {showToolTip && (
        <div className={styles.badge} data-testid="productbadges">
          <div
            aria-owns={
              isAlsoWorksInTooltipVisible
                ? alsoWorksInTooltipContentId
                : undefined
            }
            className={styles.alsoWorksInTitleContainer}
          >
            <Text className={styles.title}>
              {tryInBadgesCount === 0
                ? localizations.DetailProductDescription()
                : localizations.DetailProductDescriptionAlsoWorksIn()}
            </Text>
            <Tooltip
              content={{
                children: isExtension
                  ? localizationsWithPlaceholders.ExtensionPromptDescriptionInfoLabel(
                      { extensionName: prompt.ProviderName as string }
                    )
                  : localizations.DetailProductDescriptionAlsoWorksInInfoLabel(),
                id: alsoWorksInTooltipContentId,
              }}
              positioning="above-start"
              withArrow
              relationship="label"
              onVisibleChange={(_, data) =>
                setAlsoWorksInTooltipVisible(data.visible)
              }
            >
              <Info16Regular
                data-testid="detail-modal-tooltip"
                tabIndex={0}
                className={mergeClasses(
                  styles.tooltipIcon,
                  isAlsoWorksInTooltipVisible && styles.visibleTooltipIcon
                )}
              />
            </Tooltip>
          </div>
          <div className={styles.badgeContainer}>
            {isExtension && (
              <PromptAuthor
                prompt={prompt}
                expanded={true}
                extensionIconMap={state.extensionIconMap}
              />
            )}
            <PromptBadges
              badges={tryInByCopyBadges}
              maxNumber={5}
              displayMode="iconAndText"
            />
          </div>
        </div>
      )}
      {prompt.Origin !== "User" && prompt.DisplayCategory && (
        <div className={styles.badge}>
          <Text className={styles.category}>
            {localizations.DetailDisplayCategoryDescription()}
          </Text>
          <PromptBadges
            badges={displayCategoryBadges}
            maxNumber={5}
            displayMode="textOnly"
          />
        </div>
      )}
    </div>
  );
};
